import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { CampaignGroup } from '@tm/client-form/src/components/campaign/CampaignGroup'
import { CampaignContactInfoFields } from '@tm/types/campaign'
import { CampaignSubmit } from '../../CampaignSubmit'
import { ConsentField, ConsentFieldProps } from '../../ConsentField/ConsentField'
import { SkipButton, SkipButtonProps } from '../common/SkipButton'

export const FeedbackFieldNames = ['feedback_comment', 'contactName', 'contactEmail', 'contactPhone'] as const

export type FeedbackFieldName = typeof FeedbackFieldNames[number]

type FeedbackViewProps = {
  values: Record<FeedbackFieldName, string>
  onChange: (name: FeedbackFieldName, value: string) => void
  labels: Record<FeedbackFieldName, string>
  onSubmit: () => void
  isSubmitting?: boolean
  title: string
  description: string
  errors: Record<FeedbackFieldName, string[] | undefined>
  requiredFields: FeedbackFieldName[]
  consentFieldProps: ConsentFieldProps
  skipProps: SkipButtonProps
  contactInfoFields: CampaignContactInfoFields
}

type FeedbackFieldProps = Pick<
  FeedbackViewProps,
  'values' | 'errors' | 'labels' | 'isSubmitting' | 'onChange' | 'requiredFields'
> & {
  name: FeedbackFieldName
  type: 'text' | 'email' | 'textarea'
}

function FeedbackField(props: FeedbackFieldProps) {
  const { name, type, values, errors, labels, onChange, isSubmitting, requiredFields } = props
  const isTextarea = type === 'textarea'
  const fieldErrors = (errors[name] || []).join(', ')

  const isRequired = requiredFields.includes(name)
  const label = isRequired ? `${labels[name]} *` : labels[name]

  return (
    <TextField
      id={`q-${name}`}
      label={label}
      value={values[name]}
      error={!!fieldErrors}
      helperText={fieldErrors}
      onChange={event => onChange(name, event.target.value)}
      multiline={isTextarea}
      rows={isTextarea ? 4 : undefined}
      variant="filled"
      disabled={isSubmitting}
      type={type}
    />
  )
}

export function FeedbackView(props: FeedbackViewProps) {
  const {
    values,
    onChange,
    labels,
    onSubmit,
    isSubmitting,
    title,
    description,
    errors,
    requiredFields,
    consentFieldProps,
    skipProps,
    contactInfoFields,
  } = props

  const fieldProps = {
    values,
    errors,
    labels,
    isSubmitting,
    onChange,
    requiredFields,
  }

  return (
    <>
      <Typography variant="subtitle1">{title}</Typography>
      <Typography variant="body1">{description}</Typography>
      <CampaignGroup>
        {contactInfoFields.name && <FeedbackField name="contactName" type="text" {...fieldProps} />}
        {contactInfoFields.email && <FeedbackField name="contactEmail" type="email" {...fieldProps} />}
        {contactInfoFields.phone && <FeedbackField name="contactPhone" type="text" {...fieldProps} />}
        <FeedbackField name="feedback_comment" type="textarea" {...fieldProps} />
      </CampaignGroup>
      <ConsentField {...consentFieldProps} />
      <CampaignSubmit onClick={onSubmit} disabled={isSubmitting} />
      <SkipButton {...skipProps} />
    </>
  )
}
